import { PulseTracker } from '@client/core/utils/pulse/PulseTracker.js';
import pulse from '@finn-no/pulse-sdk';

import type { RetailMediaProduct } from '../types/index.js';

export class SponsoredProductAdPulseTracker extends PulseTracker {
  static #instance?: SponsoredProductAdPulseTracker;

  static get instance() {
    return (this.#instance ??= new SponsoredProductAdPulseTracker());
  }

  protected override readonly scope = 'Retail Media';

  readonly baseEventName = 'Sponsored Product Ad';

  protected mapDisplayAdObject({
    product,
    zone,
    categories,
    placementId
  }: SponsoredProductAdPulseTracker.Context): pulse.DisplayAd {
    const tags = [zone, ...(categories ?? [])].filter(Boolean);

    return {
      type: 'DisplayAd',
      adType: 'Product',
      adServer: 'retailmedia',
      id: `${product.adId}`,
      adPosition: placementId ?? 'unknown',
      advertiser: product.advertiser,
      brand: product.brand,
      name: product.title,
      price: product.price,
      currency: product.currency,
      ...(tags.length && { tags })
    };
  }

  async trackImpression(context: SponsoredProductAdPulseTracker.Context) {
    await this.track(() => ({
      type: 'Load',
      name: this.baseEventName,
      object: this.mapDisplayAdObject(context)
    }));
  }

  async trackViewableImpression(
    context: SponsoredProductAdPulseTracker.Context
  ) {
    await this.track(() => ({
      type: 'View',
      intent: 'Show',
      name: this.baseEventName,
      object: this.mapDisplayAdObject(context)
    }));
  }

  async trackClick(context: SponsoredProductAdPulseTracker.Context) {
    await this.track(() => {
      const { product } = context;
      const advertiserHostname = new URL(product.productUrl).hostname;

      return {
        type: 'Click',
        intent: 'Open',
        name: this.baseEventName,
        object: this.mapDisplayAdObject(context),

        target: {
          type: 'ExternalContent',
          name: `Go to ${product.title} on ${advertiserHostname}`,
          id: product.productUrl,
          url: product.productUrl,
          provider: product.advertiser,
          referralCommission: true
        }
      };
    });
  }

  async trackInfoPageClick(context: SponsoredProductAdPulseTracker.Context) {
    await this.track(() => {
      const { product } = context;
      const displayAdObject = this.mapDisplayAdObject(context);

      return {
        type: 'Click',
        intent: 'Open',
        name: `${this.baseEventName} Info Page`,

        object: {
          type: 'UIElement',
          id: `info-${product.adId}`,
          elementType: 'Link',
          position: 'Kebab menu',
          items: [displayAdObject]
        },

        // The link opens the DSA ad information page, not the advertiser's product page
        target: {
          type: 'ExternalContent',
          name: 'Go to webpage',
          id: product.dsaUrl,
          url: product.dsaUrl,
          provider: 'Vend Advertising',
          referralCommission: false
        }
      };
    });
  }
}

export namespace SponsoredProductAdPulseTracker {
  export type Context = {
    product: RetailMediaProduct;
    zone: string;
    categories?: Array<string> | undefined;
    placementId?: string;
  };
}
